import React from 'react';
import { connect } from '@gasbuddy/react';
import { FlexGrid, Header } from '@gasbuddy/react-components';

import MainLayout from '../../components/main-layout';
import CTAContainer from '../../components/cta-container';
import PageHeader from '../../components/page-header';
import StationListSelector from '../../components/station-list-selector-v2';
import BuildReportButton from '../../components/build-report-button';
import SharePageButton from '../../components/share-page-button';
import BpDateRangePicker from '../../components/bp-date-range-picker';
import ComparisonMetricSelector from '../../components/comparison-metric-selector';
import Notification from '../../components/notification';
import StationRatingsTable from '../../components/station-ratings-table';
import { getRatingsSortFn } from '../../components/table/sort-helpers';

import RoleAccessGroup from '../../../lib/constants/role-access-groups';
import BPUtils from '../../../lib/bputils';

import styles from './styles.css';

const SORTABLE_STATION_RATINGS_TABLE_COLUMNS = [

  {
    title: 'GasBuddy Rating',
    value: template => template.star_rating,
  },
  {
    title: 'Station',
    value: template => `{${template.retailer_reference} - ${template.retailer_name}}`,
  },
  {
    title: 'Total Ratings',
    value: template => template.search_impressions,
  },
  {
    title: 'Negative',
    value: template => template.brand_engagement,
  },
  {
    title: 'Neutral',
    value: template => template.station_visits,
  },
  {
    title: 'Positive',
    value: template => template.avg_dwell_time,
  },
];

function StationRatings() {
  const [isLoading, setIsLoading] = React.useState(true);
  const [stationListId, setStationListId] = React.useState(null);
  const [dateRange, setDateRange] = React.useState(null);
  const [comparisonMetric, setComparisonMetric] = React.useState(null);
  const [stationRatings, setStationRatings] = React.useState(null);
  const [comparisonStationRatings, setComparisonStationRatings] = React.useState(null);
  const [stations, setStations] = React.useState(null);
  const [pageSize, setPageSize] = React.useState(25);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [sortColumn, setSortColumn] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState(null);
  const [ratingCategories, setRatingCategories] = React.useState(null);
  const [ratingCategoryId, setRatingCategoryId] = React.useState(null);

  React.useEffect(() => {
    const queryParams = BPUtils.getQueryParameters();
    if (queryParams?.ratingCategory) {
      setRatingCategoryId(Number(queryParams.ratingCategory));
    }
    getRatingCategories();
  }, []);

  React.useEffect(() => {
    if (isLoading) {
      if (dateRange?.start && dateRange?.end && comparisonMetric && stations && stationRatings && comparisonStationRatings && ratingCategoryId) {
        setIsLoading(false);
      }
    }
    if (!isLoading) {
      if (!dateRange || !comparisonMetric || !stations || !stationRatings || !comparisonStationRatings || !ratingCategoryId) {
        setIsLoading(true);
      }
    }
  }, [dateRange?.start, dateRange?.end, comparisonMetric, stations, stationRatings, comparisonStationRatings, ratingCategoryId]);

  React.useEffect(() => {
    if (stationListId && dateRange?.start && dateRange?.end && comparisonMetric && ratingCategoryId) {
      getStationRatingsStats();
    }
  }, [stationListId, dateRange?.start, dateRange?.end, comparisonMetric, ratingCategoryId]);


  React.useEffect(() => {
    if (comparisonMetric && comparisonMetric?.metricApiName !== 'nearby' && ratingCategoryId) {
      getStationRatingsComparisonStats();
    }
  }, [comparisonMetric, dateRange?.start, dateRange?.end, ratingCategoryId]);


  React.useEffect(() => {
    if (stationListId) {
      setStations(null);
      getStations();
    }
  }, [stationListId]);

  React.useEffect(() => {
    if (comparisonMetric?.metricApiName === 'nearby' && ratingCategoryId) {
      getNearbyStationRatingsStats();
    }
  }, [pageSize, pageNumber, sortColumn, sortOrder, ratingCategoryId]);


  const handleGenerateReport = async (emails, comments) => {
    await BPUtils.api.post(
      {
        path: '/reports/station-ratings',
        data: {
          recipients: emails,
          message: comments,
          station_list_id: stationListId,
          start_date: dateRange.start,
          end_date: dateRange.end,
          comparison_target_id: comparisonMetric?.target || 0,
          comparison_type: comparisonMetric.metricApiName,
          category_id: ratingCategoryId,
        },
        requestId: 'requestStationRatingstReport',
        success: () => Notification.success('Successfully requested a report of your brand engagement'),
        error: () => Notification.error('We were unable to complete your request. Please try again or contact support if the issue persists.'),
      },
    );
  };

  const getRatingCategories = async () => BPUtils.api.get({
    path: '/rating-categories',
    requestId: 'getAllRatingCategories',
    success: (res) => {
      setRatingCategories(res);
      if (!ratingCategoryId) {
        setRatingCategoryId(res[0].id);
      }
    },
    error: () => Notification.error('We were unable to complete your request. Please try again or contact support if the issue persists.'),
  });

  const getStations = async () => BPUtils.api.get({
    requestId: 'getStationListById',
    path: `/station-lists/${stationListId}?response_type=stations_without_address`,
    success: (res) => {
      setStations(res.stations);
    },
    error: () => {
      Notification.error('Unable to fetch the stations in the selected station list');
    },
  });

  const handleOnDateRangeChange = ({ startDate, endDate }) => {
    setDateRange({ start: startDate.format('YYYY-MM-DD'), end: endDate.format('YYYY-MM-DD') });
  };

  const handleOnComparisonChange = (comparison) => {
    setComparisonMetric(comparison);
  };

  const handleOnExport = async (email) => {
    await BPUtils.api.post({
      path: `/export/stats/ratings/station-lists/${stationListId}?start_date=${dateRange.start}&end_date=${dateRange.end}`,
      data: {
        comparison_metric: comparisonMetric.metricApiName,
        email,
        comparison_target_id: comparisonMetric.target || 0,
        rating_category: ratingCategoryId,
      },
      error() {
        Notification.error('An error occurred while trying to request a data export.');
      },
      success() {
        Notification.success('The request to export your data was received successfully.');
      },
    });
  };

  const handleOnPageChange = ({ pageSize: newPageSize, pageNumber: newPageNumber }) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    if (newPageNumber !== pageNumber) {
      setPageNumber(newPageNumber);
    }
  };

  const handleOnSortChange = ({ sortColumn: newSortColumn, sortOrder: newSortOrder }) => {
    if (newSortColumn !== sortColumn) {
      setSortColumn(newSortColumn);
    }
    if (newSortOrder !== sortOrder) {
      setSortOrder(newSortOrder);
    }
  };

  const getStationRatingsStats = async () => {
    setStationRatings(null);
    await BPUtils.api.get({
      path: `/stats/ratings/station-lists/${stationListId}`,
      requestId: 'loadRatingsStats',
      data: {
        start_date: dateRange.start,
        end_date: dateRange.end,
        rating_category: ratingCategoryId,
      },
      error: () => {
        Notification.error('Unable to retrieve your Station Ratings data, please try again.');
      },
      success: ({ stats }) => {
        setStationRatings(stats);
        if (comparisonMetric?.metricApiName === 'nearby') {
          getNearbyStationRatingsStats();
        }
      },
    });
  };

  const getStationRatingsComparisonStats = async (stationIds = []) => {
    setComparisonStationRatings(null);
    await BPUtils.api.get({
      path: `/stats/ratings/${comparisonMetric.metricApiPath}?filter=national`,
      requestId: 'loadComparisonMetrics',
      data: {
        rating_category_ids: [ratingCategoryId],
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        station_ids: stationIds,
        station_list_id: stationListId,
      },
      error: () => {
        Notification.error('Unable to retrieve your comparison Station Ratings data, please try again.');
      },
      success: (res) => {
        setComparisonStationRatings(res);
      },
    });
  };

  const convertColumnTitleToStatProperty = (columnTitle) => {
    switch (columnTitle) {
      case 'Brand Impressions':
        return 'search_impressions';
      case 'Brand Engagement':
        return 'station_impressions';
      default:
        return columnTitle.toLowerCase().replace(' ', '_');
    }
  };

  const getNearbyStationRatingsStats = () => {
    const columnProperty = convertColumnTitleToStatProperty(sortColumn);
    let sortDataType = 'number';
    if (columnProperty === 'station') {
      sortDataType = 'string';
    }
    const sortFn = () => getRatingsSortFn(sortDataType, sortColumn);
    const valueFn = SORTABLE_STATION_RATINGS_TABLE_COLUMNS.find(c => c.title === sortColumn).value;
    const brandEngagementStatsMap = _.keyBy(stationRatings, 'id');
    const stationsMap = _.keyBy(stations, 'id');
    Object.keys(brandEngagementStatsMap).forEach((stationId) => {
      brandEngagementStatsMap[stationId] = {
        ...brandEngagementStatsMap[stationId],
        ...stationsMap[stationId],
      };
    });
    const tempBrandEngagementStats = Object.values(brandEngagementStatsMap);
    const sortedData = [];
    const watchedStations = tempBrandEngagementStats.filter(a => a.watched);
    const nonWatchedSations = tempBrandEngagementStats.filter(a => !a.watched);
    sortedData.push(...watchedStations.sort((a, b) => sortFn(a, b, valueFn, sortOrder === 'asc')));
    sortedData.push(...nonWatchedSations.sort((a, b) => sortFn(a, b, valueFn, sortOrder === 'asc')));
    const displayedStationIds = sortedData.slice((pageSize * (pageNumber - 1)), (pageNumber * pageSize - 1)).map(d => d.id);
    getStationRatingsComparisonStats(displayedStationIds);
  };

  const handleOnCategoryChange = categoryId => setRatingCategoryId(categoryId);

  const handleOnFilterChange = (categoryName) => {
    if (ratingCategories?.length) {
      const category = ratingCategories.find(rc => rc.name === categoryName);
      setRatingCategoryId(category.id);
    }
  };

  return (
    <MainLayout>
      <FlexGrid container>
        <FlexGrid.Column mobile={12} tablet={5}>
          <PageHeader title="Station Ratings" />
          <div>
            <StationListSelector
              onChange={setStationListId}
              storeSelectedList
              createStationListOption
              searchable
            />
          </div>
        </FlexGrid.Column>
        <FlexGrid.Column mobile={12} tablet={7}>
          <CTAContainer alignment="right">
            <FlexGrid.Column>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <BuildReportButton
                  onGenerateReport={handleGenerateReport}
                  gTagTitle="Station Ratings - Build Report"
                  hideComparisonSelector
                  hideDateRangeSelector
                  roleClasses={RoleAccessGroup.Editor}
                />
                <SharePageButton gTagTitle="Station Ratings - Share Report" />
              </div>
            </FlexGrid.Column>
            <FlexGrid.Column>
              <div className={styles.selectorContainer}>
                <div className={styles.selectorWrapper}>
                  <Header as="h6">
                    Date Range
                  </Header>
                  <BpDateRangePicker
                    className={styles.filterComponent}
                    gTagTitle="Station Ratings Date Range Picker"
                    onChange={handleOnDateRangeChange}
                  />
                </div>
                <div className={styles.selectorWrapper}>
                  <Header as="h6">
                    Compare To
                  </Header>
                  <ComparisonMetricSelector
                    id="forStationRatings"
                    key="forStationRatings"
                    className={styles.filterComponent}
                    showBrandMetric
                    gTagTitle="Station Ratings Comparison Metric Selector"
                    onChange={handleOnComparisonChange}
                  />
                </div>
              </div>
            </FlexGrid.Column>
          </CTAContainer>
        </FlexGrid.Column>
      </FlexGrid>
      {
        stationListId && (
          <FlexGrid container>
            <FlexGrid.Column>
              <StationRatingsTable
                data={stationRatings}
                comparisonData={comparisonStationRatings}
                isLoading={isLoading}
                stations={stations}
                sortable
                prioritySortOrder
                onExport={handleOnExport}
                comparison={comparisonMetric?.key}
                onPageChange={handleOnPageChange}
                onSortChange={handleOnSortChange}
                onCategoryChange={handleOnCategoryChange}
                categoryRatings={ratingCategories}
                onFilterChange={handleOnFilterChange}
              />
            </FlexGrid.Column>
          </FlexGrid>
        )
      }
    </MainLayout>
  );
}

export default connect()(StationRatings);
